<script setup>
import { ref, onMounted, onUnmounted, nextTick, defineProps, defineEmits } from 'vue';
import { editor } from 'monaco-editor/esm/vs/editor/editor.api';
import { ChevronDownIcon, ChevronUpIcon } from 'vue-tabler-icons';

const props = defineProps({ modelValue: { type: String, required: true }, title: { type: String, required: true } });

const emit = defineEmits(['update:modelValue']);

const editorEl = ref();
const collapsed = ref(true);

let editorInstance;
onMounted(async () => {
  editorInstance = editor.create(editorEl.value, {
    value: props.modelValue,
    detectIndentation: false,
    language: 'javascript',
    automaticLayout: true,
    wordWrap: true,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
  });
  editorInstance.getModel().onDidChangeContent(() => emit('update:modelValue', editorInstance.getValue()));
  await nextTick();
  editorInstance.layout();
});

onUnmounted(() => editorInstance.dispose());

async function toggleExpand() {
  collapsed.value = !collapsed.value;
  await nextTick();
  editorInstance.layout();
}
</script>

<template>
  <div class="code-editor" :class="{ 'h-10 overflow-hidden': collapsed }">
    <div
      class="code-editor__expand"
      :title="`Expand '${title}' Editor`"
      :class="{ 'rounded-t border-b-2 shadow': !collapsed, rounded: collapsed }"
      @click="toggleExpand"
    >
      <span class="code-editor__title"> {{ title }} </span>
      <chevron-down-icon v-if="collapsed" class="code-editor__expand-trigger" />
      <chevron-up-icon v-if="!collapsed" class="code-editor__collapse-trigger" />
    </div>
    <div ref="editorEl" class="editor flex-grow"></div>
  </div>
</template>

<style lang="postcss">
.code-editor {
  @apply flex h-96 flex-col gap-2 overflow-hidden rounded border border-gray-200 bg-white;
}

.code-editor__expand {
  @apply flex cursor-pointer items-center justify-between border-gray-200 bg-white px-6 py-2 text-brand-500;
}
</style>
